/**
 * Deal Form Page - Create and Edit Deal
 * Wraps DealForm component with routing and API integration
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  PencilSimple,
} from '@phosphor-icons/react';
import { DealForm } from '../components/forms';
import { LoadingPage, EmptyState } from '../components/common';
import { dealsApi, partnersApi } from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const DealFormPage = () => {
  const { dealId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const isEditMode = !!dealId;
  const cloneFromId = searchParams.get('clone');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deal, setDeal] = useState(null);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState(null);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch partners
        const partnersData = await partnersApi.getAll();
        setPartners(partnersData);
        
        // Fetch deal if editing or cloning
        if (isEditMode) {
          const dealData = await dealsApi.getById(dealId);
          setDeal(dealData);
        } else if (cloneFromId) {
          const dealData = await dealsApi.getById(cloneFromId);
          // Clear identifiers for clone
          const clonedDeal = {
            ...dealData,
            id: undefined,
            deal_name: `${dealData.deal_name} (Copy)`,
            status: 'sourcing',
            created_at: undefined,
            updated_at: undefined,
          };
          setDeal(clonedDeal);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dealId, isEditMode, cloneFromId]);

  // Handle form submission
  const handleSubmit = async (formData) => {
    try {
      setSaving(true);
      
      if (isEditMode) {
        // Update existing deal
        await dealsApi.update(dealId, formData);
        toast.success('Deal mis à jour avec succès');
        navigate(`/deal/${dealId}`);
      } else {
        // Create new deal
        const newDeal = await dealsApi.create(formData);
        toast.success('Deal créé avec succès');
        navigate(`/deal/${newDeal.id}`);
      }
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(`Échec de ${isEditMode ? 'la mise à jour' : 'la création'} du deal: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/deal/${dealId}`);
    } else {
      navigate('/pipeline');
    }
  };

  if (loading) {
    return <LoadingPage message={`Chargement ${isEditMode ? 'du deal' : 'du formulaire'}...`} />;
  }

  if (error && isEditMode) {
    return (
      <div className="space-y-6">
        <Link to="/pipeline">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={16} className="mr-1" />
            Back to Pipeline
          </Button>
        </Link>
        <EmptyState
          icon={PencilSimple}
          title="Deal introuvable"
          description={error}
          actionLabel="Go to Pipeline"
          onAction={() => navigate('/pipeline')}
        />
      </div>
    );
  }

  return (
    <div data-testid="deal-form-page" className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header - Mobile optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4">
          <Link to={isEditMode ? `/deal/${dealId}` : '/pipeline'}>
            <Button variant="ghost" size="sm" data-testid="back-btn" className="h-8 px-2 sm:h-9 sm:px-3">
              <ArrowLeft size={16} className="mr-1" />
              <span className="hidden sm:inline">{isEditMode ? 'Back to Deal' : 'Back to Pipeline'}</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </Link>
          <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
            {isEditMode ? (
              <>
                <PencilSimple size={20} className="sm:hidden" />
                <PencilSimple size={24} className="hidden sm:block" />
                <span className="hidden sm:inline">Edit Deal</span>
                <span className="sm:hidden">Edit</span>
              </>
            ) : (
              <>
                <Plus size={20} className="sm:hidden" />
                <Plus size={24} className="hidden sm:block" />
                <span className="hidden sm:inline">Create New Deal</span>
                <span className="sm:hidden">New Deal</span>
              </>
            )}
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="card-premium p-3 sm:p-6">
        <DealForm
          initialData={deal}
          partners={partners}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={saving}
          mode={isEditMode ? 'edit' : 'create'}
        />
      </div>
    </div>
  );
};

export default DealFormPage;
